import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import InfoIcon from '@src/svgs/InfoIcon';
import {
  destructureImpactScore,
  getImpactScore,
} from '@src/utility/impactScore';
import React, { useMemo } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import type { ShareableImageType } from '../../../types.ts/story';

export type PullQuoteGenerationContext = {
  source?: 'original' | 'clip';
  sourceTitle?: string;
  theme?: string;
  customPrompt?: string;
  speakers?: string[];
  goal?: string;
  audiences?: string[];
};

type Props = {
  quotes: ShareableImageType[];
  quotesSelected: Set<string>;
  generationContext?: PullQuoteGenerationContext;
};

export const PullQuoteSelectionSummaryBar = ({
  quotes,
  quotesSelected,
  generationContext,
}: Props) => {
  const selectionSummary = useMemo(() => {
    const selected = quotes.filter(q => quotesSelected.has(q.id));
    const scores = selected
      .map(q => {
        const impactScore = getImpactScore(q.metadata);
        if (!impactScore || Object.keys(impactScore).length === 0) return 0;
        return destructureImpactScore(impactScore).main ?? 0;
      })
      .filter(s => s > 0);
    const avgScore =
      scores.length > 0
        ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
        : 0;
    const speakers = [
      ...new Set(selected.map(q => q.storytellerName).filter(Boolean)),
    ];
    return {
      count: selected.length,
      avgScore,
      speakers,
    };
  }, [quotes, quotesSelected]);

  const contextTags = useMemo(() => {
    if (!generationContext) return [];
    const tags: {
      label: string;
      value: string;
      overflow?: string[];
      infoTooltip?: string;
    }[] = [];
    if (generationContext.source) {
      tags.push({
        label: 'Source',
        value: generationContext.source === 'clip' ? 'Clip' : 'Full Video',
        infoTooltip: generationContext.sourceTitle,
      });
    }
    if (generationContext.customPrompt) {
      tags.push({ label: 'Custom', value: generationContext.customPrompt });
    }
    if (generationContext.theme) {
      tags.push({ label: 'Prompt', value: generationContext.theme });
    }
    if (generationContext.speakers?.length) {
      const speakers = generationContext.speakers;
      if (speakers.length <= 2) {
        tags.push({ label: 'Speakers', value: speakers.join(', ') });
      } else {
        tags.push({
          label: 'Speakers',
          value: `${speakers.slice(0, 2).join(', ')}`,
          overflow: speakers.slice(2),
        });
      }
    }
    if (generationContext.goal) {
      tags.push({ label: 'Goal', value: generationContext.goal });
    }
    if (generationContext.audiences?.length) {
      const audiences = generationContext.audiences;
      if (audiences.length <= 2) {
        tags.push({ label: 'Audience', value: audiences.join(', ') });
      } else {
        tags.push({
          label: 'Audience',
          value: `${audiences.slice(0, 2).join(', ')}`,
          overflow: audiences.slice(2),
        });
      }
    }
    return tags;
  }, [generationContext]);

  return (
    <Bar data-testid="pull-quote-results-selection-bar">
      {contextTags.map(tag => (
        <React.Fragment key={tag.label}>
          <SummaryItem>
            <SummaryLabel>{tag.label}</SummaryLabel>
            <SummaryValue>
              {tag.value}
              {tag.overflow && (
                <>
                  {' '}
                  <OverflowHint
                    data-tooltip-id={`summary-overflow-${tag.label}`}
                  >
                    +{tag.overflow.length} more
                  </OverflowHint>
                  <Tooltip
                    id={`summary-overflow-${tag.label}`}
                    place="bottom"
                    noArrow
                    opacity={1}
                    style={{ ...MODAL_TOOLTIP_STYLE, maxWidth: '420px' }}
                    content={tag.overflow!.join(', ')}
                  />
                </>
              )}
              {tag.infoTooltip && (
                <>
                  {' '}
                  <InfoHint data-tooltip-id={`summary-info-${tag.label}`}>
                    <InfoIcon />
                  </InfoHint>
                  <Tooltip
                    id={`summary-info-${tag.label}`}
                    place="bottom"
                    noArrow
                    opacity={1}
                    style={{ ...MODAL_TOOLTIP_STYLE, maxWidth: '420px' }}
                    content={tag.infoTooltip}
                  />
                </>
              )}
            </SummaryValue>
          </SummaryItem>
          <SummaryDivider />
        </React.Fragment>
      ))}
      <SummaryItem>
        <SummaryLabel>Selected</SummaryLabel>
        <SummaryValue>
          {selectionSummary.count > 0
            ? `${selectionSummary.count} quote${selectionSummary.count !== 1 ? 's' : ''}`
            : '0'}
        </SummaryValue>
      </SummaryItem>
      <SummaryDivider />
      <SummaryItem>
        <SummaryLabel>Avg. Impact</SummaryLabel>
        {selectionSummary.avgScore > 0 ? (
          <SummaryHighlight>{selectionSummary.avgScore}</SummaryHighlight>
        ) : (
          <SummaryValue>–</SummaryValue>
        )}
      </SummaryItem>
    </Bar>
  );
};

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  margin-top: 8px;
  padding: 6px 0 6px 8px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

const SummaryLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
`;

const SummaryValue = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
`;

const SummaryHighlight = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  color: var(--primary-hover);
`;

const OverflowHint = styled.span`
  color: var(--text-muted);
  cursor: default;
  &:hover {
    color: var(--foreground);
  }
`;

const InfoHint = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--text-muted);
  cursor: default;
  svg {
    width: 14px;
    height: 14px;
  }
  &:hover {
    color: var(--foreground);
  }
`;

const SummaryDivider = styled.div`
  width: 1px;
  height: 16px;
  background-color: var(--border);
  flex-shrink: 0;
`;
